/**
 * @file Builds the transfer table rows by pairing each source investment with its overridden target investment.
 */
import { useMemo } from "react";
import sortBy from "lodash.sortby";
import groupBy from "lodash.groupby";

import getProfileId, { type ProfileInvestment } from "./getProfileId";
import type { CustomTarget } from "../types";

type SourceInvestment = ProfileInvestment & {
  id: string;
};

type TransferFields = {
  profileId: string;
  isIncluded: boolean;
  sourceInvestmentId: string | null;
};

type Options<I extends SourceInvestment> = {
  customTargets: CustomTarget[];
  sourceInvestments: I[];
  targetInvestments: I[];
};

/**
 * Builds the transfer table rows, linking each target investment that overrides a source investment by
 * profile id or by a custom link, and marks a source investment as excluded once it is overridden.
 */
const useDataSource = <I extends SourceInvestment>({
  customTargets,
  sourceInvestments,
  targetInvestments,
}: Options<I>): (I & TransferFields)[] => {
  return useMemo(() => {
    const extendedSourceInvestments = sourceInvestments
      .map((investment): I & TransferFields => ({
        ...investment,
        profileId: getProfileId(investment),
        isIncluded: true,
        sourceInvestmentId: null,
      }));

    const extendedTargetInvestments = targetInvestments
      .map((investment): I & TransferFields => ({
        ...investment,
        profileId: getProfileId(investment),
        isIncluded: true,
        sourceInvestmentId: null,
      }));

    const sourceInvestmentsGroup: Record<string, (I & TransferFields)[]> =
      groupBy(extendedSourceInvestments, "profileId");

    const overrideTargetInvestments = extendedTargetInvestments
      .filter(({ id, profileId }) =>
        !!sourceInvestmentsGroup[profileId] ||
        !!customTargets.find(({ targetInvestmentId }) => id === targetInvestmentId),
      )
      .map((investment) => {
        const customTarget = customTargets
          .find(({ targetInvestmentId }) => investment.id === targetInvestmentId);

        if (customTarget) {
          const { sourceInvestmentId } = customTarget;

          const sourceInvestment = extendedSourceInvestments
            .find(({ id }) => id === sourceInvestmentId);

          const { profileId } = sourceInvestment;

          return {
            ...investment,
            profileId,
            sourceInvestmentId,
          };
        }

        const { profileId } = investment;
        const [ sourceInvestment ] = sourceInvestmentsGroup[profileId];

        return {
          ...investment,
          sourceInvestmentId: sourceInvestment.id,
        };
      });

    const overrideInvestmentsGroup: Record<string, (I & TransferFields)[]> =
      groupBy(overrideTargetInvestments, "profileId");

    extendedSourceInvestments.forEach((investment) => {
      const { profileId } = investment;

      const overrideInvestments = overrideInvestmentsGroup[profileId] || [];
      const hasOverrideInvestments = overrideInvestments.length > 0;
      const hasMultipleOverrideInvestments = overrideInvestments.length > 1;

      if (hasMultipleOverrideInvestments) {
        throw new Error(`Multiple investments found for profile ${profileId} in target project`);
      }

      investment.isIncluded = !hasOverrideInvestments;
    });

    const dataSource: (I & TransferFields)[] = sortBy([
      ...extendedSourceInvestments,
      ...overrideTargetInvestments,
    ], "profileId");

    return dataSource;
  }, [
    customTargets,
    sourceInvestments,
    targetInvestments,
  ]);
};

export default useDataSource;
